import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  ServiceTitanJob,
  ServiceTitanAppointment,
  Customer,
  FieldIssue,
  Location,
  Technician,
  Note,
  ServiceTitanJobCancelEvent,
  CallOutcomeEnum,
  ServiceTitanPortalV2Company,
  pick,
  TimezoneAbbreviationEnum,
  SetRequiredNonNullable
} from '_lib/api/apiTypes.shim';

import { RootState } from '_lib/redux/store';
import { DateUtils } from '_lib/utils/date.utils';
import { getTradeForNewCompany } from '_lib/utils/helpers';
import { ClearIssueItem, removeIssues } from '_lib/utils/validationErrorUtils';

import type { SharedPhoneCallContext, SwitchProviderCompanyPayload } from '_lib/types/PhoneCall.types';
import type { RequireAtLeastOne } from 'type-fest';

export interface PhoneCallContext
  extends Pick<
    SharedPhoneCallContext,
    'callOutcome' | 'internalNotes' | 'sendEscalationEmail' | 'trade' | 'serviceType'
  > {
  /** Selected job for the call */
  job: ServiceTitanJob | null;
  /** synchronized cancel data related to the selected Job if the outcome is Cancellation */
  jobCancelEvent: ServiceTitanJobCancelEvent | null;
  /** synchronized job history notes related to the Job AND modified during call time frame */
  jobHistoryNotes: Note[];
  /** synchronized appointments related to the Job AND modified during call time frame */
  otherAppointments: ServiceTitanAppointment[];
  /** Selected appointment for the call. Should always be an appointment for the selected job */
  appointment: ServiceTitanAppointment | null;
  /** synchronized Technician assigned to the selected appointment */
  assignedTechnician: Technician | null;
  /** Customer for the call. Either auto set via Job OR manually set by agent if no Job is selected */
  customer: Customer | null;
  /** Selected location for the call. Either auto set via Job or manually by agent if no Job is selected */
  location: Location | null;
  /** List of all appointments related to the Job, but not necessarily modified by the agent. These appointments are meant to populate appointment drop-down */
  appointmentsForJob: ServiceTitanAppointment[];
  /** List of all locations related to the Customer. These locations are meant to populate location drop-down */
  locationsForCustomer: Location[];
  /** Previous Appointment Date Time for Reschedule */
  previousAppointmentDateTime: string | null;
  /** timezone of the company for the current call */
  companyTimezone: TimezoneAbbreviationEnum;
}

export const toSharedPhoneCallContext = (phoneCallContext: PhoneCallContext): SharedPhoneCallContext => {
  const { appointment, companyTimezone, callOutcome, previousAppointmentDateTime } = phoneCallContext;

  let previousAppointmentDate: string | null = null;
  if (callOutcome === CallOutcomeEnum.Reschedule && previousAppointmentDateTime) {
    previousAppointmentDate = DateUtils.toDateString(previousAppointmentDateTime, { timezone: companyTimezone });
  } else if (callOutcome !== CallOutcomeEnum.NewAppointment && appointment?.start) {
    previousAppointmentDate = DateUtils.toDateString(appointment.start, { timezone: companyTimezone });
  }

  const isAppointmentDateOutcome =
    callOutcome === CallOutcomeEnum.NewAppointment || callOutcome === CallOutcomeEnum.Reschedule;

  return {
    callOutcome,
    appointmentDateTime: (isAppointmentDateOutcome && appointment?.start) || null,
    previousAppointmentDate,
    internalNotes: phoneCallContext.internalNotes,
    sendEscalationEmail: phoneCallContext.sendEscalationEmail,
    trade: phoneCallContext.trade,
    serviceType: phoneCallContext.serviceType,
    // shared props that this phone call slice does not track, so we default them
    sendCompanyEmail: false,
    notes: ''
  };
};

/* Subset of data that we can safely carry over when switching tenants, or upon a re-initialization from error */
const safeDataKeys = [
  'callOutcome',
  'internalNotes',
  'sendEscalationEmail',
  'serviceType'
] as const satisfies (keyof PhoneCallContext)[];

type InitialPhoneCallContext = SetRequiredNonNullable<Partial<PhoneCallContext>, 'companyTimezone'>;

const initPhoneCallContext = (ctx: InitialPhoneCallContext): PhoneCallContext => ({
  /* shared PhoneCall Props */
  callOutcome: ctx.callOutcome,
  internalNotes: ctx.internalNotes || '',
  previousAppointmentDateTime: ctx.previousAppointmentDateTime || null,
  sendEscalationEmail: ctx.sendEscalationEmail !== undefined ? ctx.sendEscalationEmail : false,
  serviceType: ctx.serviceType || null,
  trade: ctx.trade || null,
  /* specific ST Portal PhoneCall Props */
  job: ctx.job || null,
  jobCancelEvent: ctx.jobCancelEvent || null,
  appointment: ctx.appointment || null,
  customer: ctx.customer || null,
  location: ctx.location || null,
  appointmentsForJob: ctx.appointmentsForJob || [],
  locationsForCustomer: ctx.locationsForCustomer || [],
  jobHistoryNotes: ctx.jobHistoryNotes || [],
  otherAppointments: ctx.otherAppointments || [],
  assignedTechnician: ctx.assignedTechnician || null,
  companyTimezone: ctx.companyTimezone
});

interface StPortalPhoneCallSlice {
  phoneCallContext: PhoneCallContext;
  phoneCallFieldIssues: FieldIssue[];
  phoneCallNonFieldErrors: string[];
}

const initState = (phoneCallContext: InitialPhoneCallContext): StPortalPhoneCallSlice => ({
  phoneCallContext: initPhoneCallContext(phoneCallContext),
  phoneCallFieldIssues: [],
  phoneCallNonFieldErrors: []
});

export const stPortalPhoneCallSlice = createSlice({
  name: 'stPortalPhoneCallSlice',
  initialState: initState({ companyTimezone: TimezoneAbbreviationEnum.EST }),
  reducers: {
    initialize: (
      state,
      action: PayloadAction<{ company: ServiceTitanPortalV2Company; options?: { keepExistingCallData: boolean } }>
    ) => {
      const { company, options } = action.payload;

      const trade = getTradeForNewCompany(
        (options?.keepExistingCallData && state.phoneCallContext.trade) || null,
        company
      );

      return initState({
        ...(options?.keepExistingCallData ? pick(state.phoneCallContext, safeDataKeys) : {}),
        companyTimezone: company.timezone,
        trade
      });
    },
    switchDifferentTenantCompany: (state, action: PayloadAction<ServiceTitanPortalV2Company>) => {
      return {
        ...initState({
          ...pick(state.phoneCallContext, safeDataKeys),
          companyTimezone: action.payload.timezone,
          trade: getTradeForNewCompany(state.phoneCallContext.trade, action.payload)
        }),
        phoneCallFieldIssues: state.phoneCallFieldIssues,
        phoneCallNonFieldErrors: state.phoneCallNonFieldErrors
      };
    },
    switchDifferentProviderCompany: (_state, action: PayloadAction<SwitchProviderCompanyPayload>) => {
      const { company, notes, internalNotes, ...phoneCallContext } = action.payload || {};
      return initState({
        ...phoneCallContext,
        // Prevent losing notes when switching to ST Portal V2, as it has no 'notes' input
        internalNotes: `${notes}\n${internalNotes}`.trim(),
        companyTimezone: company.timezone,
        trade: getTradeForNewCompany(phoneCallContext.trade, company)
      });
    },
    /** Used to ensure we have a valid trade selected when switching companies */
    normalizeTradeFromCompany: (state, action: PayloadAction<ServiceTitanPortalV2Company>) => {
      state.phoneCallContext = {
        ...state.phoneCallContext,
        trade: getTradeForNewCompany(state.phoneCallContext.trade, action.payload)
      };
    },
    setPartialPhoneCallContext(
      state,
      action: PayloadAction<{
        updates: RequireAtLeastOne<Partial<PhoneCallContext>>;
        clearIssues?: ClearIssueItem[];
      }>
    ) {
      state.phoneCallContext = { ...state.phoneCallContext, ...action.payload.updates };
      if (state.phoneCallFieldIssues.length && action.payload.clearIssues?.length) {
        const updatedIssues = action.payload.clearIssues.reduce(
          (acc, item) => removeIssues({ ...(typeof item === 'string' ? { issuePath: item } : item), issues: acc }),
          state.phoneCallFieldIssues
        );
        if (updatedIssues.length !== state.phoneCallFieldIssues.length) state.phoneCallFieldIssues = updatedIssues;
      }
    },
    setPhoneCallFieldIssues(state, action: PayloadAction<FieldIssue[]>) {
      state.phoneCallFieldIssues = action.payload;
    },
    setPhoneCallNonFieldErrors(state, action: PayloadAction<string[]>) {
      state.phoneCallNonFieldErrors = action.payload;
    },
    reset: () => initState({ companyTimezone: TimezoneAbbreviationEnum.EST })
  },
  selectors: {
    selectPhoneCallContext: (state) => state.phoneCallContext,
    selectPhoneCallFieldIssues: (state) => state.phoneCallFieldIssues,
    selectPhoneCallNonFieldErrors: (state) => state.phoneCallNonFieldErrors
  }
});

export const {
  setPartialPhoneCallContext,
  setPhoneCallFieldIssues,
  setPhoneCallNonFieldErrors,
  initialize,
  switchDifferentTenantCompany,
  switchDifferentProviderCompany,
  normalizeTradeFromCompany,
  reset
} = stPortalPhoneCallSlice.actions;

export const { selectPhoneCallContext, selectPhoneCallFieldIssues, selectPhoneCallNonFieldErrors } =
  stPortalPhoneCallSlice.getSelectors((rootState: RootState) => rootState.stPortalPhoneCallSlice);
